// ─── What: alert markers ──────────────────────────────────────────────────────
//
// One shape per severity — rounded rect / triangle / diamond — so severity reads
// before colour does, and colour-blind users get the same signal.
//
// The chart shows the FIRST alert of each consecutive (severity, type) run
// rather than every alert: a stuck condition fires repeatedly and would
// otherwise bury the plot under identical marks.

import type { AlertItem, AlertsOverlayData } from '../../../../api/types';
import { ALERT_SEVERITY_COLORS } from '../../../../utils/colors';
import { clampToDomain, offScaleDirection } from '../../chartYDomain';
import { placeTooltip, buildAlertTooltipHtml } from '../../chartTooltips';
import type { ChartChrome, ChartGroup, DrawCtx } from '../drawContext';

/** Half-size of the outer shape. Bigger reads as more prominent. */
const S = 14;

/** Tidy path numbers — long decimals bloat the DOM for no visual gain. */
const np = (x: number) => parseFloat(x.toFixed(2));

interface Cfg {
    /** Outline colour. */
    border: string;
    fill: string;
    /** Always bright enough to read against the fill. */
    iconCol: string;
}

function severityConfig(isDark: boolean): Record<string, Cfg> {
    const c = ALERT_SEVERITY_COLORS;
    return {
        info: {
            border: c.info,
            fill: isDark ? `${c.info}38` : `${c.info}29`,
            iconCol: isDark ? '#4da6ff' : '#0047a8',
        },
        warning: {
            border: c.warning,
            fill: isDark ? `${c.warning}38` : `${c.warning}29`,
            iconCol: isDark ? '#ffb74d' : '#e65100',
        },
        critical: {
            border: c.critical,
            fill: isDark ? `${c.critical}38` : `${c.critical}29`,
            iconCol: isDark ? '#ef9a9a' : '#fff',
        },
    };
}

/** First alert of each consecutive (severity, type) run, inside the window. */
export function dedupeAlertRuns(
    alerts: AlertItem[],
    xDomain: [Date, Date]
): AlertItem[] {
    const chronological = [...alerts].sort(
        (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime()
    );
    const visible: AlertItem[] = [];
    let lastKey = '';
    for (const alert of chronological) {
        const key = `${alert.severity}__${alert.alert_type}`;
        if (key !== lastKey) {
            const t = new Date(alert.ts);
            if (t >= xDomain[0] && t <= xDomain[1]) visible.push(alert);
            lastKey = key;
        }
    }
    return visible;
}

function drawInfoShape(g: ChartGroup, tx: number, ty: number, cfg: Cfg): void {
    const rw = S * 2.2;
    const rh = S * 2.0;
    g.append('rect')
        .attr('x', np(tx - rw / 2))
        .attr('y', np(ty - rh / 2))
        .attr('width', rw)
        .attr('height', rh)
        .attr('rx', 4)
        .attr('fill', cfg.fill)
        .attr('stroke', cfg.border)
        .attr('stroke-width', 2)
        .style('pointer-events', 'none');

    const ir = S * 0.78;
    g.append('circle')
        .attr('cx', tx)
        .attr('cy', ty)
        .attr('r', ir)
        .attr('fill', 'none')
        .attr('stroke', cfg.iconCol)
        .attr('stroke-width', 1.8)
        .style('pointer-events', 'none');
    g.append('circle')
        .attr('cx', tx)
        .attr('cy', np(ty - ir * 0.4))
        .attr('r', 1.8)
        .attr('fill', cfg.iconCol)
        .style('pointer-events', 'none');
    // Stem starts below the dot with a clear gap, or the glyph reads as an "l".
    g.append('line')
        .attr('x1', tx)
        .attr('y1', np(ty - ir * 0.12))
        .attr('x2', tx)
        .attr('y2', np(ty + ir * 0.5))
        .attr('stroke', cfg.iconCol)
        .attr('stroke-width', 2.2)
        .attr('stroke-linecap', 'round')
        .style('pointer-events', 'none');
}

function drawWarningShape(
    g: ChartGroup,
    tx: number,
    ty: number,
    cfg: Cfg
): void {
    const tw = S * 2.6;
    const th = S * 2.3;
    // Centroid of an equilateral triangle sits 1/3 up from the base, so centring
    // it on (tx, ty) means apex at -2/3 and base at +1/3.
    const apex = np(ty - (th * 2) / 3);
    const baseY = np(ty + th / 3);
    g.append('path')
        .attr(
            'd',
            `M${tx},${apex} L${np(tx + tw / 2)},${baseY} L${np(tx - tw / 2)},${baseY} Z`
        )
        .attr('fill', cfg.fill)
        .attr('stroke', cfg.border)
        .attr('stroke-width', 2)
        .attr('stroke-linejoin', 'round')
        .style('pointer-events', 'none');

    const iconCY = np(ty - th / 10);
    const bodyH = th * 0.3;
    const bodyW = 2.6;

    g.append('rect')
        .attr('x', np(tx - bodyW / 2))
        .attr('y', np(iconCY - bodyH / 2))
        .attr('width', bodyW)
        .attr('height', bodyH)
        .attr('rx', 1.2)
        .attr('fill', cfg.iconCol)
        .style('pointer-events', 'none');
    g.append('circle')
        .attr('cx', tx)
        .attr('cy', np(iconCY + bodyH / 2 + 3.0))
        .attr('r', 1.9)
        .attr('fill', cfg.iconCol)
        .style('pointer-events', 'none');
}

function drawCriticalShape(
    g: ChartGroup,
    tx: number,
    ty: number,
    cfg: Cfg
): void {
    const dw = S * 2.2;
    const dh = S * 2.4;
    g.append('path')
        .attr(
            'd',
            `M${tx},${np(ty - dh / 2)} L${np(tx + dw / 2)},${ty} L${tx},${np(ty + dh / 2)} L${np(tx - dw / 2)},${ty} Z`
        )
        .attr('fill', cfg.fill)
        .attr('stroke', cfg.border)
        .attr('stroke-width', 2)
        .attr('stroke-linejoin', 'round')
        .style('pointer-events', 'none');

    // Lightning bolt: upper half slants right, lower half slants left, with an
    // inner notch at each mid so the two halves read as one glyph.
    const bH = S * 1.15;
    const bW = S * 0.72;
    const boltPath = [
        `M${np(tx + bW * 0.28)},${np(ty - bH * 0.5)}`,
        `L${np(tx - bW * 0.28)},${np(ty - bH * 0.02)}`,
        `L${np(tx + bW * 0.1)},${np(ty - bH * 0.02)}`,
        `L${np(tx - bW * 0.28)},${np(ty + bH * 0.5)}`,
        `L${np(tx + bW * 0.28)},${np(ty + bH * 0.02)}`,
        `L${np(tx - bW * 0.1)},${np(ty + bH * 0.02)}`,
        'Z',
    ].join(' ');
    g.append('path')
        .attr('d', boltPath)
        .attr('fill', cfg.iconCol)
        .attr('stroke', 'none')
        .style('pointer-events', 'none');
}

export interface AlertOverlayDeps {
    alertsOverlay: AlertsOverlayData;
    chrome: ChartChrome;
}

export function drawAlertOverlay(ctx: DrawCtx, deps: AlertOverlayDeps): void {
    const { svg, layout, colors, xScale, xDomain, yScale, probDomain, tooltipRef } =
        ctx;
    const { alertsOverlay, chrome } = deps;
    const { isDark, tc } = colors;

    const alertGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.sig})`)
        .attr('class', 'alert-overlay');

    const cfgBySeverity = severityConfig(isDark);

    for (const alert of dedupeAlertRuns(alertsOverlay.alerts, xDomain)) {
        const tx = xScale(new Date(alert.ts));
        // Without a probability at trigger time there is no meaningful height —
        // park it mid-panel rather than dropping the alert.
        const ty: number =
            alert.current_yes_prob != null
                ? yScale(
                      clampToDomain(alert.current_yes_prob, probDomain.domain)
                  )
                : layout.probHeight / 2;

        const cfg = cfgBySeverity[alert.severity] ?? cfgBySeverity.info;
        const g = alertGroup.append('g') as ChartGroup;

        if (alert.severity === 'info') drawInfoShape(g, tx, ty, cfg);
        else if (alert.severity === 'warning')
            drawWarningShape(g, tx, ty, cfg);
        else drawCriticalShape(g, tx, ty, cfg);

        // An alert pinned to the axis edge gets a dashed border, so its position
        // reads as "beyond the axis" rather than exact.
        if (
            alert.current_yes_prob != null &&
            offScaleDirection(alert.current_yes_prob, probDomain.domain)
        ) {
            g.attr('class', 'alert-marker--offscale')
                .selectAll('rect, path')
                .attr('stroke-dasharray', '3 2');
        }

        const tooltipHtml = buildAlertTooltipHtml(alert, tc);
        g.append('circle')
            .attr('cx', tx)
            .attr('cy', ty)
            .attr('r', S * 1.5)
            .attr('fill', 'transparent')
            .attr('stroke', 'none')
            .style('cursor', 'pointer')
            .on('mouseenter', (event: MouseEvent) => {
                chrome.suppressForMarker();
                g.attr(
                    'transform',
                    `translate(${tx},${ty}) scale(1.3) translate(${-tx},${-ty})`
                );
                placeTooltip(tooltipRef.current, event, tooltipHtml);
            })
            .on('mousemove', (event: MouseEvent) => {
                placeTooltip(
                    tooltipRef.current,
                    event,
                    tooltipRef.current?.innerHTML ?? ''
                );
            })
            .on('mouseleave', () => {
                g.attr('transform', null);
                chrome.hideTooltip();
            });
    }

    alertGroup.raise();
}
